import React, { useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { DateTime } from 'luxon'
import PropTypes from 'prop-types'

import {
  Box, Image, ListItem, StatusBadge, Tag, Text,
} from '@campaignhub/suit-theme'

import { useSetState } from '@campaignhub/react-hooks'
import PageContext from '@contexts/pageContext'

import 'react-tippy/dist/tippy.css'
import { Tooltip } from 'react-tippy'

import Icons from '@components/Icons'
import { faCheckCircle, faExclamationTriangle, faInfoCircle } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import useBlobMetadata, { AI_GENERATED_META_KEY } from '@hooks/useBlobMetadata'
import customMetaData from '@functions/customMetaData'
import { isFeatureEnabled } from '@functions/features'

const defaultState = {
  VSIRComment: null,
}

const GridItem = (props) => {
  const {
    badgeColor,
    badgeText,
    callbacks,
    consolidatedContent,
    content,
    disableAssetLink,
    fileUrl,
    iconName,
    link,
    multipleSelection,
    name,
    notSentYet,
    selected,
    showDescription,
    showDetails,
  } = props

  const [state, setState] = useSetState(defaultState)
  const { VSIRComment } = state
  const customMetaValues = useBlobMetadata(fileUrl)
  const isAiGenerated = Boolean(customMetaValues[AI_GENERATED_META_KEY])

  // URL-only assets (e.g. Virtual_Tour) are a link out to the tour, not an internal asset.
  const directUrl = content?.directUrl

  const { selectContent } = callbacks || {}

  const { assetComments } = useSelector(reduxState => reduxState.entities)

  const {
    callbacks: { toggleGalleryOverlay },
  } = useContext(PageContext)

  useEffect(() => {
    const VSIRCommentId = content?.comments?.reverse()?.find(comment => assetComments[comment]?.details?.find(detail => detail?.entityFieldType?.name === 'VSIRStatus'))

    setState({ VSIRComment: Object.values(assetComments)?.find(assetComment => assetComment.id === VSIRCommentId)?.comment || null })
  }, [])

  const detailsListItems = (
    <>
      <ListItem
        boxProps={{ padding: 'large' }}
        flexDirection="column"
        showAngleIcon
        disableHover={disableAssetLink}
      >
        <Text variant="ellipsis" fontSize="medium" fontWeight="600" width={180} title={name}>
          {name}
        </Text>
        <Box flexDirection="row" marginTop="medium" alignItems="center">
          <Box alignItems="center" justifyContent="flexStart" width="auto" paddingRight="medium">
            <Icons name={iconName} />
          </Box>
          <StatusBadge color={badgeColor} text={badgeText} />
          {isAiGenerated && (
            <Tag boxProps={{ width: 'fit-content', fontSize: 'xsmall', marginLeft: 'small' }}>
              AI
            </Tag>
          )}
        </Box>
        {/* The hidden state reserves the line so cards don't jump, so the gate has to
            drop the whole element \u2014 leaving it would change every card's height. */}
        {isFeatureEnabled('delivered-indicator') && (
          <Text
            fontSize="xsmall"
            color="bodyFontLightColor"
            marginTop="xsmall"
            style={notSentYet ? undefined : { visibility: 'hidden' }}
            aria-hidden={!notSentYet}
          >
            {notSentYet ? `v${content.version} - not sent yet` : '\u00A0'}
          </Text>
        )}
      </ListItem>
      {showDescription && !directUrl && (
        <ListItem
          boxProps={{
            borderTop: '1px solid',
            padding: 'large',
          }}
          disableHover
          flexDirection="column"
        >
          <Box
            flexDirection="row"
            justifyContent="space-between"
          >
            <Text fontSize="small">{content?.description}</Text>
            {Object.keys(customMetaValues).length ? (
              <Tooltip
                html={(
                  <div style={{ textAlign: 'left' }}>
                    <Text fontSize="small">File Type: {content?.file?.fileType.name}</Text>
                    {Object.entries(customMetaData).map(([key, value]) => (
                      Object.keys(customMetaValues).find(x => x.includes(key)) && (
                        <Text key={key} fontSize="small">
                          {value}: {key === 'datetimeoriginal'
                            ? DateTime.fromFormat(customMetaValues[Object.keys(customMetaValues)
                              .find(x => x.includes(key))], 'yyyy:MM:dd HH:mm:ss')
                              .toFormat('MM/dd/yy HH:mm')
                            : key === 'size' ? `${(customMetaValues[Object.keys(customMetaValues).find(x => x.includes(key))] / (1024 * 1024)).toFixed(2)} MB`
                              : customMetaValues[Object.keys(customMetaValues).find(x => x.includes(key))]}
                        </Text>
                      )
                    ))}
                  </div>
                )}
                trigger="mouseenter"
                arrow
                arrowSize="regular"
                duration={0}
              >
                <FontAwesomeIcon icon={faInfoCircle} color="black" size="sm" />
              </Tooltip>
            ) : null}
            {content.file?.fileType?.name?.toLowerCase() === 'pdf' && content?.file?.pdfFlattenRequests?.length > 0
              && !content?.file?.pdfFlattenRequests?.some((y) => y.generatedAt !== null) ? (
              <Tooltip
                html={<Text fontSize="small">Flattened file not yet generated</Text>}
                trigger="mouseenter"
                arrow
                arrowSize="regular"
                duration={0}
              >
                <FontAwesomeIcon icon={faExclamationTriangle} color="black" size="sm" />
              </Tooltip>
            ) : null}
          </Box>
        </ListItem>
      )}
    </>
  )

  return (
    <Box
      border={showDetails ? '1px solid' : 0}
      borderColor="lineColor"
      borderRadius={5}
      flexDirection="column"
      key={content.id}
    >
      <Image
        backgroundPosition="center center"
        backgroundRepeat="no-repeat"
        backgroundSize="contain"
        borderRadius={showDetails ? '5px 5px 0 0' : '5px'}
        boxProps={{ backgroundColor: 'hoverLightGrey' }}
        height={140}
        url={content?.file?.previewGeneratedAt ? content.previewUrl : ''}
        width={'100%' || 200}
      >
        <Box
          as={!multipleSelection && (directUrl || (fileUrl && consolidatedContent)) ? 'a' : null}
          height="100%"
          href={directUrl || fileUrl}
          onClick={() => (multipleSelection ? selectContent() : (consolidatedContent || directUrl) ? null : toggleGalleryOverlay(content.id))}
          position="relative"
          rel={directUrl ? 'noreferrer' : null}
          style={{ cursor: multipleSelection ? 'pointer' : '' }}
          target="_blank"
        >
          {VSIRComment && (
            <Box
              alignItems="start"
              color="bodyFontColor"
              display="grid"
              height="100%"
              justifyContent="end"
              position="absolute"
              width="100%"
            >
              <Tag boxProps={{ width: 'fit-content', fontSize: 'xsmall', marginTop: 'small' }}>
                {VSIRComment}
              </Tag>
            </Box>
          )}
          {!content?.file?.previewGeneratedAt && (
            <Box
              alignItems="center"
              color="bodyFontColor"
              display="grid"
              height="100%"
              justifyContent="center"
              position="absolute"
              width="100%"
            >
              <Icons name={iconName} size={50} width="100%" />
            </Box>
          )}
          {selected && (
            <Box
              alignItems="center"
              backgroundColor="rgba(0, 0, 0, 0.4)"
              borderRadius={showDetails ? '5px 5px 0 0' : '5px'}
              color="bodyFontColor"
              display="grid"
              justifyContent="center"
              width="100%"
              zIndex={1}
            >
              <Box
                alignItems="center"
                borderRadius={20}
                color="white"
                fontSize="xsmall"
                justifyContent="center"
                width="100%"
              >
                <FontAwesomeIcon icon={faCheckCircle} size="3x" />
              </Box>
            </Box>
          )}
        </Box>
      </Image>
      {showDetails && (
        <Box flexDirection="column">
          {directUrl ? (
            <a href={directUrl} rel="noreferrer" style={{ textDecoration: 'none' }} target="_blank">
              {detailsListItems}
            </a>
          ) : (
            <Link
              to={link}
              onClick={(e) => {
                if (disableAssetLink) {
                  e.preventDefault()
                }
              }}
              style={{ textDecoration: 'none' }}
            >
              {detailsListItems}
            </Link>
          )}
        </Box>
      )}
      {!showDetails && (
        <Text fontSize="small" fontWeight={600} paddingTop="medium">
          {content.fileName}
        </Text>
      )}
    </Box>
  )
}

GridItem.propTypes = {
  badgeColor: PropTypes.string,
  badgeText: PropTypes.string,
  callbacks: PropTypes.object,
  consolidatedContent: PropTypes.bool,
  content: PropTypes.object,
  disableAssetLink: PropTypes.bool,
  fileUrl: PropTypes.string,
  iconName: PropTypes.string,
  link: PropTypes.string,
  multipleSelection: PropTypes.bool,
  name: PropTypes.string,
  notSentYet: PropTypes.bool,
  selected: PropTypes.bool,
  showDescription: PropTypes.bool,
  showDetails: PropTypes.bool,
}

export default GridItem
